/**
 * ContentTransliterator — runtime transliteration of hardcoded uz-Cyrl text
 * nodes and selected attributes to uz-Latn.
 *
 * Why: ~3.8k hardcoded Cyrillic strings exist across 91 files (see i18n
 * audit). Rewriting every call site to a translation key is out of scope for
 * this sprint; instead, when the active locale is uz-Latn, we transliterate
 * text content and attributes in-place. The operation is idempotent because
 * toUzbekLatin() only changes Cyrillic codepoints.
 *
 * Scope controls:
 *  - Only runs while locale === "uz-Latn".
 *  - Form control VALUES (input/textarea) and password fields are never
 *    touched; their placeholder/title/aria-label attributes ARE translated.
 *  - Skips nodes opted out via `data-no-translit`.
 *  - Attribute whitelist: placeholder, title, aria-label, alt, data-tip.
 */
import { useEffect, useRef } from "react";
import { useI18n } from "./I18nContext";
import { toUzbekLatin } from "./uzLatin";

// Text nodes inside these elements are user data / code, not UI labels.
const TEXT_SKIP_SELECTOR = [
  "input",
  "textarea",
  "script",
  "style",
  "code",
  "pre",
  "svg",
  "[data-no-translit]",
].join(",");

// Elements whose own attributes must be skipped entirely.
const ATTR_SKIP_SELECTOR = ["script", "style", "[data-no-translit]"].join(",");

const ATTRIBUTE_NAMES = ["placeholder", "title", "aria-label", "alt", "data-tip"] as const;

function transliterateTree(root: Node) {
  if (!(root instanceof Node)) return;
  const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.parentElement?.closest(TEXT_SKIP_SELECTOR)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes: Text[] = [];
  let current = textWalker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) textNodes.push(current as Text);
    current = textWalker.nextNode();
  }

  for (const node of textNodes) {
    const converted = toUzbekLatin(node.nodeValue ?? "");
    if (converted !== node.nodeValue) node.nodeValue = converted;
  }

  const elements = root instanceof Element && root.querySelectorAll ? root.querySelectorAll("*") : [];
  for (const el of Array.from(elements)) {
    if (!(el instanceof Element)) continue;
    if (el.closest(ATTR_SKIP_SELECTOR) === el) continue;
    if (el instanceof HTMLElement || el instanceof SVGElement) {
      for (const name of ATTRIBUTE_NAMES) {
        const value = el.getAttribute(name);
        if (value) {
          const converted = toUzbekLatin(value);
          if (converted !== value) el.setAttribute(name, converted);
        }
      }
    }
  }
}

export function ContentTransliterator() {
  const { locale } = useI18n();
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (locale !== "uz-Latn") return;

    const run = () => {
      rafRef.current = 0;
      transliterateTree(document.body);
    };

    rafRef.current = requestAnimationFrame(run);
    const observer = new MutationObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(run);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      observer.disconnect();
    };
  }, [locale]);

  return null;
}
